import { createContext, useContext } from 'react';

import type { IManagementReportDataset } from '@commenda/api';

import type { BucketOverrides, ReportingBucket } from '../types/buckets';
import type { IFactTable } from '../types/metrics';
import type { IPeriodFilter, MonthKey } from '../types/period';
import type { IReportItem, IReportSavedView } from '../types/report';

type Updater<T> = T | ((previous: T) => T);

export interface IReportContextValue {
  dataset: IManagementReportDataset;
  currency: string;
  booksClosedThrough: MonthKey;
  /** MANAGEMENT_REPORT_COLUMN_LAYOUT is on for this company. */
  columnLayout: boolean;
  /** MANAGEMENT_REPORT_INLINE_OVERRIDE is on for this company. */
  inlineOverride: boolean;
  table: IFactTable;
  bucketOverrides: BucketOverrides;
  setBucketOverride: (lineId: string, bucket: ReportingBucket | null) => void;
  filter: IPeriodFilter;
  setFilter: (next: Updater<IPeriodFilter>) => void;
  items: IReportItem[];
  setItems: (next: Updater<IReportItem[]>) => void;
  views: IReportSavedView[];
  activeViewId: string;
  /** Layout or filter differs from the active saved view. */
  isDirty: boolean;
  openView: (id: string) => void;
  saveView: () => void;
  saveViewAs: (name: string) => void;
  renameView: (id: string, name: string) => void;
  deleteView: (id: string) => void;
  resetToDefault: () => void;
}

export const ReportContext = createContext<IReportContextValue | null>(null);

export const useReport = (): IReportContextValue => {
  const value = useContext(ReportContext);
  if (!value) throw new Error('useReport must be used inside ReportProvider');
  return value;
};
